import { useMemo, useState } from 'react';
import { format, setHours, setMinutes } from 'date-fns';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { eventsApi } from '@/api/events';
import { meetingsApi } from '@/api/meetings';
import { notesApi } from '@/api/notes';
import { tasksApi } from '@/api/tasks';
import { cn } from '@/lib/cn';
import { useCalendarPermissions } from '@/hooks/preferences/useCalendarPermissions';
import { hasOrgWideEventPermission } from '@/lib/calendarPermissions';
import { useCreateOrgWideEventMutation } from '@/hooks/calendar/useOrgMutations';
import { EventRemindersPicker } from '@/components/calendar/forms/EventRemindersPicker';
import { DEFAULT_EVENT_REMINDER_OFFSETS, normalizeReminderOffsets } from '@/lib/eventReminders';

type CreateKind = 'event' | 'meeting' | 'task' | 'note' | 'orgEvent';

type Props = {
  isOpen: boolean;
  selectedDate: Date | null;
  activeCalendarId?: string;
  onClose: () => void;
};

export function CalendarCreateModal({ isOpen, selectedDate, activeCalendarId, onClose }: Props) {
  const queryClient = useQueryClient();
  const perms = useCalendarPermissions();
  const [kind, setKind] = useState<CreateKind>('event');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [location, setLocation] = useState('');
  const [eventDescription, setEventDescription] = useState('');
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [allDay, setAllDay] = useState(false);
  const [meetingDuration, setMeetingDuration] = useState(60);
  const [taskNotes, setTaskNotes] = useState('');
  const [taskPriority, setTaskPriority] = useState<'NONE' | 'LOW' | 'MEDIUM' | 'HIGH'>('NONE');
  const [notePinned, setNotePinned] = useState(false);
  const [reminderOffsets, setReminderOffsets] = useState<number[]>(() => [...DEFAULT_EVENT_REMINDER_OFFSETS]);

  const showOrgEventTab = hasOrgWideEventPermission(perms, 'create');

  const defaultRange = useMemo(() => {
    if (!selectedDate) return { start: '', end: '' };
    const base = setMinutes(setHours(selectedDate, 9), 0);
    const end = setMinutes(setHours(selectedDate, 10), 0);
    return {
      start: format(base, "yyyy-MM-dd'T'HH:mm"),
      end: format(end, "yyyy-MM-dd'T'HH:mm"),
    };
  }, [selectedDate]);

  const eventMutation = useMutation({
    mutationFn: eventsApi.create,
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['events'] }); void queryClient.invalidateQueries({ queryKey: ['org', 'wide-events'] }); onClose(); resetForm(); },
  });
  const taskMutation = useMutation({
    mutationFn: tasksApi.create,
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['tasks'] }); onClose(); resetForm(); },
  });
  const meetingMutation = useMutation({
    mutationFn: meetingsApi.create,
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['meetings'] }); onClose(); resetForm(); },
  });
  const noteMutation = useMutation({
    mutationFn: notesApi.create,
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ['notes'] }); onClose(); resetForm(); },
  });
  const orgWideEventMutation = useCreateOrgWideEventMutation();

  function resetForm() {
    setTitle(''); setBody(''); setLocation(''); setEventDescription('');
    setStartAt(''); setEndAt(''); setAllDay(false); setMeetingDuration(60);
    setTaskNotes(''); setTaskPriority('NONE'); setNotePinned(false);
    setReminderOffsets([...DEFAULT_EVENT_REMINDER_OFFSETS]);
  }

  if (!isOpen || !selectedDate) return null;

  const actualStart = startAt || defaultRange.start;
  const actualEnd = endAt || defaultRange.end;
  const isSaving =
    eventMutation.isPending || meetingMutation.isPending ||
    taskMutation.isPending || noteMutation.isPending || orgWideEventMutation.isPending;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (kind === 'meeting') {
      if (!title.trim()) return;
      const safeDuration = Number.isFinite(meetingDuration) && meetingDuration > 0 ? meetingDuration : 60;
      meetingMutation.mutate({ name: title.trim(), scheduledAt: new Date(actualStart).toISOString(), duration: safeDuration });
      return;
    }
    if (kind === 'note') {
      if (!body.trim()) return;
      noteMutation.mutate({ body: body.trim(), happensAt: new Date(actualStart).toISOString(), pinned: notePinned, ...(activeCalendarId ? { calendarId: activeCalendarId } : {}), reminderOffsetsMinutes: normalizeReminderOffsets(reminderOffsets) });
      return;
    }
    if (kind === 'orgEvent') {
      if (!title.trim()) return;
      orgWideEventMutation.mutate({ title: title.trim(), startsAt: new Date(actualStart).toISOString(), endsAt: new Date(actualEnd).toISOString(), allDay, venue: location.trim() ? location.trim() : null, description: eventDescription.trim() ? eventDescription.trim() : null, reminderOffsetsMinutes: normalizeReminderOffsets(reminderOffsets) }, { onSuccess: () => { onClose(); resetForm(); } });
      return;
    }
    if (!title.trim()) return;
    if (kind === 'event') {
      eventMutation.mutate({ title: title.trim(), startsAt: new Date(actualStart).toISOString(), endsAt: new Date(actualEnd).toISOString(), allDay, ...(activeCalendarId ? { calendarId: activeCalendarId } : {}), location: location.trim() ? location.trim() : null, description: eventDescription.trim() ? eventDescription.trim() : null, reminderOffsetsMinutes: normalizeReminderOffsets(reminderOffsets) });
      return;
    }
    taskMutation.mutate({ title: title.trim(), dueAt: new Date(actualStart).toISOString(), status: 'NEEDS_ACTION', notes: taskNotes.trim() ? taskNotes.trim() : null, priority: taskPriority, ...(activeCalendarId ? { calendarId: activeCalendarId } : {}), reminderOffsetsMinutes: normalizeReminderOffsets(reminderOffsets) });
  };

  const tabs: Array<{ key: CreateKind; label: string }> = [
    { key: 'event', label: 'Event' },
    { key: 'meeting', label: 'Meeting' },
    { key: 'task', label: 'Task' },
    { key: 'note', label: 'Note' },
    ...(showOrgEventTab ? [{ key: 'orgEvent' as const, label: 'Org Event' }] : []),
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0f172b]/40 p-3 backdrop-blur-[3px] sm:p-4">
      <button type="button" aria-label="Close create modal" className="absolute inset-0 h-full w-full cursor-default" onClick={onClose} />
      <div className="custom-scrollbar relative z-10 max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-3xl bg-white bg-[radial-gradient(ellipse_60%_35%_at_100%_0%,rgba(129,140,248,0.24),transparent_70%)] shadow-[0_24px_80px_-30px_rgba(15,23,42,0.45)] dark:bg-card dark:bg-[radial-gradient(ellipse_60%_35%_at_100%_0%,rgba(139,124,255,0.16),transparent_70%)]">
        <div className="px-5 pb-2 pt-6 sm:px-8">
          <div className="mb-5 flex items-start justify-between gap-4">
            <div>
              <h3 className="flex items-center gap-3 text-lg font-bold tracking-tight text-[#0f172b] dark:text-white">
                <span aria-hidden="true" className="h-5 w-1 rounded-full bg-gradient-to-b from-[#5B6CF6] to-[#8B5CF6]" />
                Create new item
              </h3>
              <p className="mt-1.5 pl-4 text-xs font-medium text-slate-500 dark:text-slate-400">{format(selectedDate, 'EEEE, MMM d, yyyy')}</p>
            </div>
            <button type="button" onClick={onClose} className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F3F4F8] text-slate-500 transition-colors hover:bg-[#E8EAF2] dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10" aria-label="Close create modal">
              <CloseIcon />
            </button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {tabs.map((tab) => (
              <button key={tab.key} type="button" onClick={() => setKind(tab.key)} className={cn('rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-wide transition-all', kind === tab.key ? 'bg-gradient-to-r from-[#5B6CF6] to-[#8B5CF6] text-white' : 'bg-[#F3F4F8] text-slate-600 hover:bg-[#EEF2FF] hover:text-[#5B47E8] dark:bg-white/5 dark:text-slate-300 dark:hover:bg-[#1C1F3F] dark:hover:text-[#8B7CFF]')}>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={submit} className="space-y-5 px-5 pb-6 pt-4 sm:px-8 sm:pb-8">
          {kind === 'event' || kind === 'meeting' || kind === 'task' || kind === 'orgEvent' ? (
            <FormInput label={kind === 'task' ? 'Task title' : kind === 'meeting' ? 'Meeting title' : 'Title'} value={title} onChange={(e) => setTitle(e.target.value)} placeholder={kind === 'task' ? 'Enter task title' : 'Add a clear title'} required />
          ) : null}

          {kind !== 'task' && kind !== 'meeting' ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <FormInput type="datetime-local" label="Start time" value={actualStart} onChange={(e) => setStartAt(e.target.value)} />
              {kind === 'event' || kind === 'orgEvent' ? (
                <FormInput type="datetime-local" label="End time" value={actualEnd} onChange={(e) => setEndAt(e.target.value)} />
              ) : null}
            </div>
          ) : (
            <FormInput type="datetime-local" label={kind === 'meeting' ? 'Meeting start' : 'Task due time'} value={actualStart} onChange={(e) => setStartAt(e.target.value)} />
          )}

          {kind === 'meeting' ? (
            <FormInput type="number" label="Duration (minutes)" min={15} step={15} value={meetingDuration} onChange={(e) => setMeetingDuration(Number(e.target.value))} placeholder="60" />
          ) : null}

          {kind === 'event' || kind === 'orgEvent' ? (
            <FormSwitch label="All day" description="Hide specific start/end hours" checked={allDay} onChange={setAllDay} />
          ) : null}

          {kind === 'event' || kind === 'task' || kind === 'note' || kind === 'orgEvent' ? (
            <SectionCard title="Reminders">
              <EventRemindersPicker value={reminderOffsets} onChange={setReminderOffsets} disabled={isSaving} hideLabel />
            </SectionCard>
          ) : null}

          {kind === 'event' || kind === 'orgEvent' ? (
            <SectionCard title="Event details">
              <FormInput label={kind === 'orgEvent' ? 'Venue (optional)' : 'Location (optional)'} value={location} onChange={(e) => setLocation(e.target.value)} placeholder={kind === 'orgEvent' ? 'Conference hall' : 'Add location'} />
              <FormTextarea label="Description (optional)" value={eventDescription} onChange={(e) => setEventDescription(e.target.value)} placeholder="Add context or notes for attendees..." />
            </SectionCard>
          ) : null}

          {kind === 'note' ? (
            <SectionCard title="Note">
              <FormSwitch label="Pin this note" checked={notePinned} onChange={setNotePinned} />
              <FormTextarea label="Content" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write your note..." required />
            </SectionCard>
          ) : null}

          {kind === 'task' ? (
            <SectionCard title="Task details">
              <FormTextarea label="Notes (optional)" value={taskNotes} onChange={(e) => setTaskNotes(e.target.value)} placeholder="Explain the task..." />
              <FormSelect label="Priority" value={taskPriority} onChange={(e) => setTaskPriority(e.target.value as typeof taskPriority)} options={[{ value: 'NONE', label: 'None' }, { value: 'LOW', label: 'Low' }, { value: 'MEDIUM', label: 'Medium' }, { value: 'HIGH', label: 'High' }]} />
            </SectionCard>
          ) : null}

          <div className="flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="rounded-full bg-[#F3F4F8] px-6 py-2.5 text-sm font-semibold text-slate-600 transition-colors hover:bg-[#E8EAF2] dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10">Cancel</button>
            <button type="submit" disabled={isSaving} className="rounded-full bg-gradient-to-r from-[#5B6CF6] to-[#8B5CF6] px-7 py-2.5 text-sm font-semibold text-white shadow-lg shadow-[#6D5CF6]/30 transition hover:opacity-95 active:scale-95 disabled:opacity-60">
              {isSaving ? 'Saving...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CloseIcon() {
  return <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>;
}

type BaseFieldProps = { label: string; required?: boolean };

function FieldShell({ label, required, children }: BaseFieldProps & { children: React.ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <p className="pl-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
        {label}{required ? <span className="ml-1 text-rose-500">*</span> : null}
      </p>
      {children}
    </label>
  );
}

function FormInput({ label, required, className, ...props }: BaseFieldProps & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <FieldShell label={label} required={required}>
      <input {...props} required={required} className={cn('h-12 w-full rounded-full border border-transparent bg-[#F3F4F8] px-5 text-sm font-medium text-[#0f172b] outline-none transition-all placeholder:text-slate-400 focus:border-[#5B6CF6] focus:bg-white focus:ring-4 focus:ring-[#5B6CF6]/10 dark:bg-white/5 dark:text-white dark:focus:bg-white/10', className)} />
    </FieldShell>
  );
}

function FormTextarea({ label, required, className, rows = 4, ...props }: BaseFieldProps & React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <FieldShell label={label} required={required}>
      <textarea {...props} required={required} rows={rows} className={cn('w-full resize-none rounded-3xl border border-transparent bg-[#F3F4F8] px-5 py-3 text-sm font-medium text-[#0f172b] outline-none transition-all placeholder:text-slate-400 focus:border-[#5B6CF6] focus:bg-white focus:ring-4 focus:ring-[#5B6CF6]/10 dark:bg-white/5 dark:text-white dark:focus:bg-white/10', className)} />
    </FieldShell>
  );
}

function FormSelect({ label, options, className, ...props }: BaseFieldProps & React.SelectHTMLAttributes<HTMLSelectElement> & { options: Array<{ value: string; label: string }> }) {
  return (
    <FieldShell label={label}>
      <select {...props} className={cn('h-12 w-full rounded-full border border-transparent bg-[#F3F4F8] px-5 text-sm font-medium text-[#0f172b] outline-none transition-all focus:border-[#5B6CF6] focus:bg-white focus:ring-4 focus:ring-[#5B6CF6]/10 dark:bg-white/5 dark:text-white dark:focus:bg-white/10', className)}>
        {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    </FieldShell>
  );
}

function FormSwitch({ label, description, checked, onChange }: { label: string; description?: string; checked: boolean; onChange: (next: boolean) => void }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} className="flex w-full items-center justify-between rounded-3xl bg-[#F3F4F8] px-5 py-3 text-left transition-colors hover:bg-[#ECEEF5] dark:bg-white/5 dark:hover:bg-white/10">
      <span>
        <p className="text-sm font-semibold text-[#0f172b] dark:text-white">{label}</p>
        {description ? <p className="text-xs font-medium text-slate-500 dark:text-slate-400">{description}</p> : null}
      </span>
      <span className={cn('relative inline-flex h-6 w-11 items-center rounded-full transition-colors', checked ? 'bg-gradient-to-r from-[#5B6CF6] to-[#8B5CF6]' : 'bg-slate-300 dark:bg-white/15')}>
        <span className={cn('inline-block h-5 w-5 transform rounded-full bg-white transition-transform', checked ? 'translate-x-5' : 'translate-x-0.5')} />
      </span>
    </button>
  );
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-4 rounded-3xl bg-[#F6F7FB] p-5 dark:bg-white/[0.03]">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">{title}</p>
      {children}
    </section>
  );
}
